import React from 'react'
import CurrencyInputImport, { CurrencyInputOnChangeValues } from 'react-currency-input-field'
import { CURRENCY_ITEMS } from '../utils/constants'
import { interopDefault } from '../utils/interop'
import { cn } from '../utils/style'

// react-currency-input-field is CJS with no `exports` map; unwrap its default
// so the vite 8 (rolldown) production bundle renders the component, not `{ default }`.
const CurrencyInput = interopDefault(CurrencyInputImport)

interface CurrencyInputFieldProps {
  label?: string
  value: string | number | null | undefined
  currency: string
  onValueChange?: (val: CurrencyInputOnChangeValues | undefined) => void
  onCurrencyChange?: (val: string) => void
  placeholder?: string
  currencyLabel?: string
  id?: string
  name?: string
  // Display-only: no currency picker, no editing, no field chrome
  readOnly?: boolean
  className?: string
  inputClassName?: string
}

export const CurrencyInputField: React.FC<CurrencyInputFieldProps> = ({
  label,
  value,
  currency,
  onValueChange,
  onCurrencyChange,
  placeholder,
  currencyLabel = 'Currency',
  id,
  name,
  readOnly = false,
  className,
  inputClassName,
}) => {
  const currentCurrency = CURRENCY_ITEMS[currency as keyof typeof CURRENCY_ITEMS]

  return (
    <div className={cn('flex min-w-0 flex-col', className)}>
      {label ? (
        <label className="mb-1 block text-left font-medium" translate="no">
          {label}
        </label>
      ) : null}
      <div
        className={cn(
          'flex min-w-0 overflow-hidden',
          readOnly
            ? 'items-baseline justify-end gap-1'
            : 'rounded-md border bg-surface focus-within:ring-2 focus-within:ring-border-strong',
        )}
      >
        {readOnly ? (
          <span className="shrink-0 text-xs font-medium text-text-secondary" translate="no">
            {currentCurrency?.label ?? currency}
          </span>
        ) : (
          <div className="border-r border-border">
            <select
              value={currency}
              onChange={(e) => onCurrencyChange?.(e.target.value)}
              className="h-full cursor-pointer bg-transparent px-3 py-2 text-text-primary focus:outline-none"
              aria-label={currencyLabel}
              translate="no"
            >
              {Object.entries(CURRENCY_ITEMS).map(([key, item]) => (
                <option key={key} value={key}>
                  {item.label}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className={cn('min-w-0', !readOnly && 'flex-1')}>
          <CurrencyInput
            key={currency} // Force re-render when currency changes
            id={id}
            name={name}
            decimalSeparator={currentCurrency?.decimalSeparator || '.'}
            groupSeparator={currentCurrency?.groupSeparator || ','}
            placeholder={placeholder}
            value={value ?? ''}
            decimalsLimit={2}
            readOnly={readOnly}
            tabIndex={readOnly ? -1 : undefined}
            onValueChange={(value, name, values) => {
              onValueChange?.(values)
            }}
            className={cn(
              'min-w-0 truncate border-0 bg-transparent text-text-primary placeholder:font-thin placeholder:text-text-placeholder focus:outline-none focus:ring-0',
              readOnly ? 'pointer-events-none max-w-full p-0 [field-sizing:content]' : 'w-full p-2',
              inputClassName,
            )}
          />
        </div>
      </div>
    </div>
  )
}
